import React, { createContext, useContext, useEffect, useState } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [themeMode, setThemeMode] = useState('dark');

    useEffect(() => {
        // Check for saved theme in localStorage or default to dark mode
        const savedTheme = localStorage.getItem('themeMode');
        if (savedTheme) {
            setThemeMode(savedTheme);
        } else {
            // No saved preference yet - respect the system preference
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setThemeMode(prefersDark ? 'dark' : 'light');
        }
    }, []);

    useEffect(() => {
        // Save theme preference to localStorage
        localStorage.setItem('themeMode', themeMode);
    }, [themeMode]);

    const toggleTheme = () => {
        setThemeMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    };

    const theme = createTheme({
        palette: {
            mode: themeMode,
            primary: {
                main: "#e5912bff",
            },
            secondary: {
                main: "#0f5804ff",
            },
            background: {
                default: themeMode === 'dark' ? "#121212" : "#d8d8d8ff",
                paper: themeMode === 'dark' ? "#1e1e1e" : "#ffffffff",
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: themeMode === 'dark' ? "#121212" : "#d8d8d8ff",
                        backgroundSize: '20px 20px',
                        backgroundPosition: '0 0, 10px',
                    },
                },
            },
        },
    });

    return (
        <ThemeContext.Provider value={{ theme, themeMode, toggleTheme }}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};